/**
 * Front-end party-match shim.
 *
 * Mirrors the logic in `firebase-functions/functions/lib/partyId.js`. The
 * dispatch asked us NOT to duplicate logic; we share the schema (phone+email
 * compound query, lower-case email, trim phone) and document this file as the
 * client-side mirror. Until a callable Cloud Function is wired (Sprint 3),
 * the front-end performs the same compound query directly via Firestore —
 * security rules permit Sales/Admin reads on `clients/`.
 */

import {
  collection,
  getDocs,
  limit as fsLimit,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Client, FirmId, PartyMatch } from '../types/client';

function normalizeEmail(email: string): string {
  return (email || '').toString().trim().toLowerCase();
}
function normalizePhone(phone: string): string {
  return (phone || '').toString().trim();
}

function randomPartyId(): string {
  // Use SubtleCrypto.randomUUID where available
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback
  const buf = new Uint8Array(16);
  (crypto || (window as unknown as { crypto: Crypto }).crypto).getRandomValues(buf);
  return Array.from(buf).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function findOrGenerateParty(args: {
  phone: string;
  email: string;
}): Promise<PartyMatch & { matchedClient?: Client }> {
  const phone = normalizePhone(args.phone);
  const email = normalizeEmail(args.email);
  if (!phone || !email) {
    return {
      matched: false,
      partyId: randomPartyId(),
      reason: 'missing_key_for_match',
    };
  }
  const q = query(
    collection(db, 'clients'),
    where('phone', '==', phone),
    where('email', '==', email),
    fsLimit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) {
    return { matched: false, partyId: randomPartyId() };
  }
  const doc = snap.docs[0];
  const data = doc.data() as Client;
  return {
    matched: true,
    partyId: data.partyId || randomPartyId(),
    matchedClientId: doc.id,
    matchedFirmId: (data.firmId as FirmId) || undefined,
    matchedClient: data,
  };
}

/** Search by phone OR email separately — used to surface looser matches. */
export async function findByPhoneOrEmail(args: {
  phone?: string;
  email?: string;
}): Promise<{ phoneMatches: Client[]; emailMatches: Client[] }> {
  const phone = normalizePhone(args.phone || '');
  const email = normalizeEmail(args.email || '');
  const out = { phoneMatches: [] as Client[], emailMatches: [] as Client[] };
  if (phone) {
    const snap = await getDocs(
      query(collection(db, 'clients'), where('phone', '==', phone), fsLimit(5))
    );
    out.phoneMatches = snap.docs.map((d) => d.data() as Client);
  }
  if (email) {
    const snap = await getDocs(
      query(collection(db, 'clients'), where('email', '==', email), fsLimit(5))
    );
    out.emailMatches = snap.docs.map((d) => d.data() as Client);
  }
  return out;
}
